import {
  BusinessAssetMetaData,
  BusinessAssetType,
  DepreciableCategory,
  ITransaction,
  TransactionSplit,
} from "../../types";
import { CUSTOM_ASSET_CLASS, DEPRECIABLE_CATEGORY_CODES } from "./constants";

export const isImmovableAsset = (assetType?: BusinessAssetType): boolean =>
  assetType === BusinessAssetType.IMMOVABLE;

export const isIntangibleAsset = (assetType?: BusinessAssetType): boolean =>
  assetType === BusinessAssetType.INTANGIBLE;

export const isCustomAssetClass = (assetClass?: string): boolean =>
  assetClass === CUSTOM_ASSET_CLASS;

export const isNoteRequired = (
  assetType?: BusinessAssetType,
  assetClass?: string
): boolean => !(isIntangibleAsset(assetType) || isCustomAssetClass(assetClass));

export const shouldShowWithoutDepreciationsCheckbox = (
  assetType?: BusinessAssetType,
  assetClass?: string
): boolean => {
  const isAssetTypeIntangible = isIntangibleAsset(assetType);
  const isAssetTypeImmovable = isImmovableAsset(assetType);

  return (
    isAssetTypeIntangible ||
    (isAssetTypeImmovable &&
      assetClass === "Andere (z. B. grundstücksgleiche Rechte)") ||
    (assetType === BusinessAssetType.MOVABLE_OTHERS &&
      assetClass === "Sonstige")
  );
};

export const isDepreciableCategoryCode = (
  categoryCode?: string | null
): categoryCode is DepreciableCategory =>
  !!categoryCode && DEPRECIABLE_CATEGORY_CODES.includes(categoryCode);

type TransactionWithBusinessAssets<
  T extends Pick<BusinessAssetMetaData, "businessAssetableId">
> = Pick<ITransaction, "id" | "categoryCode"> & {
  businessAssets?: T[];
  splits?: Partial<Pick<TransactionSplit, "uuid" | "categoryCode">>[];
};

export const getToBeDeletedBusinessAssets = <
  T extends Pick<BusinessAssetMetaData, "businessAssetableId">
>(
  transaction: TransactionWithBusinessAssets<T>
): T[] => {
  if (!transaction.businessAssets?.length) {
    return [];
  }

  const isDepreciableTransaction = isDepreciableCategoryCode(
    transaction.categoryCode
  );

  return transaction.businessAssets.filter((businessAsset) => {
    if (
      isDepreciableTransaction &&
      businessAsset.businessAssetableId === transaction.id
    ) {
      return false;
    }

    return !transaction.splits?.some(
      (split) =>
        split.uuid === businessAsset.businessAssetableId &&
        isDepreciableCategoryCode(split.categoryCode)
    );
  });
};
